import analyticsMd from './content/analytics.md?raw';
import auditLogMd from './content/audit-log.md?raw';
import dataProcessingMd from './content/data-processing.md?raw';
import gettingStartedMd from './content/getting-started.md?raw';
import glossaryMd from './content/glossary.md?raw';
import linksAndRoutingMd from './content/links-and-routing.md?raw';
import partnerApiMd from './content/partner-api.md?raw';
import privacyNoticeMd from './content/privacy-notice.md?raw';
import recallsMd from './content/recalls.md?raw';
import rolesAndPermissionsMd from './content/roles-and-permissions.md?raw';
import termsOfServiceMd from './content/terms-of-service.md?raw';
import type { HelpArticle } from './help-types.js';

export const HELP_ARTICLES: readonly HelpArticle[] = [
  {
    slug: 'getting-started',
    title: 'Getting Started: Organisations, Workspaces & Products',
    summary:
      'How your MyPlayer account is structured across organisations, brand workspaces, GTIN identifiers, and batch/serial qualifiers.',
    category: 'guides',
    readTimeMinutes: 3,
    markdown: gettingStartedMd,
  },
  {
    slug: 'links-and-routing',
    title: 'Links and Routing',
    summary:
      'How default links, GS1 linkType relations, language and region rules, scheduled campaigns, and RFC 9264 Linksets work.',
    category: 'guides',
    readTimeMinutes: 4,
    markdown: linksAndRoutingMd,
  },
  {
    slug: 'recalls',
    title: 'Product Recalls',
    summary:
      'How emergency product safety recalls override normal routing, how fast recalls take effect, and who can activate or clear them.',
    category: 'guides',
    readTimeMinutes: 3,
    markdown: recallsMd,
  },
  {
    slug: 'roles-and-permissions',
    title: 'Roles and Permissions',
    summary:
      'Role-based access control across Organisation Admins, Workspace Managers, Campaign Editors, Compliance Officers, Viewers, and Partner API keys.',
    category: 'governance',
    readTimeMinutes: 4,
    markdown: rolesAndPermissionsMd,
  },
  {
    slug: 'audit-log',
    title: 'Audit Log & Tamper-Evidence',
    summary:
      'What is recorded in your organisation audit log and how sequential SHA-256 hash chaining detects tampering.',
    category: 'governance',
    readTimeMinutes: 3,
    markdown: auditLogMd,
  },
  {
    slug: 'analytics',
    title: 'Scan Analytics & Privacy Approach',
    summary:
      'How daily scan counts, cache hit rates, countries, and device categories are aggregated without ever storing raw IP addresses.',
    category: 'governance',
    readTimeMinutes: 3,
    markdown: analyticsMd,
  },
  {
    slug: 'partner-api',
    title: 'Partner API Reference',
    summary:
      'Authenticate with workspace API keys, respect rate limits, and automate GTIN staging, qualifiers, and recalls.',
    category: 'reference',
    readTimeMinutes: 4,
    markdown: partnerApiMd,
  },
  {
    slug: 'glossary',
    title: 'Glossary of Key Terms',
    summary:
      'Plain-English definitions for GTIN, GS1 Digital Link, linkType, Resolver, Redirect (307), Linkset, Recall, Workspace, and Qualifier.',
    category: 'reference',
    readTimeMinutes: 2,
    markdown: glossaryMd,
  },
  {
    slug: 'privacy-notice',
    title: 'Privacy Notice',
    summary:
      'How MyPlayer Ltd processes personal data for Admin Console users and protects shopper privacy on QR code scans.',
    category: 'legal',
    readTimeMinutes: 4,
    markdown: privacyNoticeMd,
  },
  {
    slug: 'terms-of-service',
    title: 'Terms of Service',
    summary:
      'Terms governing enterprise use of the MyPlayer GS1 Digital Link Platform, GS1 licensing, destination safety, and recalls.',
    category: 'legal',
    readTimeMinutes: 5,
    markdown: termsOfServiceMd,
  },
  {
    slug: 'data-processing',
    title: 'Data Processing Summary',
    summary:
      'Article 28 GDPR processing summary covering categories of data, technical security measures, sub-processors, and retention schedules.',
    category: 'legal',
    readTimeMinutes: 4,
    markdown: dataProcessingMd,
  },
];

export function getHelpArticleBySlug(
  slug: string | undefined,
  articles: readonly HelpArticle[] = HELP_ARTICLES,
): HelpArticle | undefined {
  if (!slug) return undefined;
  const normalized = slug.trim().toLowerCase();
  return articles.find((article) => article.slug.toLowerCase() === normalized);
}
